import React, { useState, useEffect, useCallback } from 'react';
import { youtubeAPI } from '../services/api';

/**
 * YouTube 연동 카드 컴포넌트
 *
 * - OAuth 연결/해제
 * - 모니터링 시작/중지
 * - 실시간 시청자 수
 * - 채팅 참여자 목록
 * - 큰손 입장 알림
 */
function YouTubeConnect({ user, onUserUpdate }) {
  const [loading, setLoading] = useState(false);
  const [monitoring, setMonitoring] = useState(false);
  const [error, setError] = useState('');
  const [monitorStatus, setMonitorStatus] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);

  const isConnected = user?.youtube_connected;

  // 모니터링 상태 폴링
  const fetchMonitorStatus = useCallback(async () => {
    if (!isConnected) return;
    try {
      const res = await youtubeAPI.monitorStatus();
      setMonitorStatus(res.data);
      setMonitoring(res.data.monitoring);
    } catch (err) {
      console.error('[YouTube] Monitor status error:', err);
    }
  }, [isConnected]);

  // 채팅 참여자 목록 조회
  const fetchParticipants = useCallback(async () => {
    if (!monitoring) return;
    try {
      const res = await youtubeAPI.participants();
      setParticipants(res.data.participants || []);
    } catch (err) {
      console.error('[YouTube] Participants error:', err);
    }
  }, [monitoring]);

  useEffect(() => {
    fetchMonitorStatus();
    const interval = setInterval(fetchMonitorStatus, 10000);
    return () => clearInterval(interval);
  }, [fetchMonitorStatus]);

  useEffect(() => {
    if (monitoring && showParticipants) {
      fetchParticipants();
      const interval = setInterval(fetchParticipants, 10000);
      return () => clearInterval(interval);
    }
  }, [monitoring, showParticipants, fetchParticipants]);

  // YouTube OAuth 연결 시작
  const handleConnect = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await youtubeAPI.auth();
      window.location.href = res.data.authUrl;
    } catch (err) {
      setError('YouTube 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // YouTube 연결 해제
  const handleDisconnect = async () => {
    setLoading(true);
    try {
      await youtubeAPI.disconnect();
      setMonitoring(false);
      setMonitorStatus(null);
      setParticipants([]);
      if (onUserUpdate) onUserUpdate();
    } catch (err) {
      setError('연결 해제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 모니터링 시작
  const handleStartMonitor = async () => {
    setLoading(true);
    setError('');
    try {
      await youtubeAPI.startMonitor();
      setMonitoring(true);
      // 즉시 상태 조회
      await fetchMonitorStatus();
    } catch (err) {
      setError(err.response?.data?.error || '모니터링 시작에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 모니터링 중지
  const handleStopMonitor = async () => {
    setLoading(true);
    try {
      await youtubeAPI.stopMonitor();
      setMonitoring(false);
      setMonitorStatus(null);
      setParticipants([]);
    } catch (err) {
      setError('모니터링 중지에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => new Intl.NumberFormat('ko-KR').format(num || 0);

  return (
    <div className="card">
      <h2 style={{ marginBottom: 8, fontSize: '1rem' }}>YouTube 연동</h2>

      {error && <div className="error-msg" style={{ marginBottom: 8 }}>{error}</div>}

      {!isConnected ? (
        <>
          <p style={{ color: '#8B8B9E', marginBottom: 16, fontSize: '0.85rem' }}>
            YouTube 계정을 연결하면 라이브 시청자 수, 채팅 참여자, 수퍼챗을 실시간으로 추적합니다.
          </p>
          <button
            className="btn btn-primary"
            onClick={handleConnect}
            disabled={loading}
            style={{ background: '#FF0000', fontSize: '0.85rem' }}
          >
            {loading ? '연결 중...' : 'YouTube 연결'}
          </button>
        </>
      ) : (
        <>
          {/* 채널 정보 + 상태 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <span className={`status-dot ${monitoring ? 'connected' : 'disconnected'}`} />
              <strong style={{ fontSize: '0.9rem' }}>{user.youtube_channel_name || 'YouTube 채널'}</strong>
              <span style={{ color: '#8B8B9E', fontSize: '0.8rem', marginLeft: 6 }}>
                {monitoring ? '모니터링 중' : '연결됨'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {!monitoring ? (
                <button className="btn btn-primary" onClick={handleStartMonitor} disabled={loading}
                  style={{ padding: '4px 12px', fontSize: '0.8rem' }}>
                  {loading ? '...' : '모니터링 시작'}
                </button>
              ) : (
                <button className="btn btn-outline" onClick={handleStopMonitor} disabled={loading}
                  style={{ padding: '4px 12px', fontSize: '0.8rem' }}>
                  중지
                </button>
              )}
              <button className="btn btn-danger" onClick={handleDisconnect} disabled={loading}
                style={{ padding: '4px 12px', fontSize: '0.8rem' }}>
                해제
              </button>
            </div>
          </div>

          {/* 모니터링 중일 때: 실시간 통계 */}
          {monitoring && monitorStatus && (
            <>
              {/* 방송 제목 */}
              {monitorStatus.broadcastTitle && (
                <div style={{ fontSize: '0.8rem', color: '#8B8B9E', marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {monitorStatus.broadcastTitle}
                </div>
              )}

              {/* 실시간 수치 카드 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div style={{ background: 'rgba(255,0,0,0.08)', borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#FF4444', marginBottom: 2 }}>실시간 시청자</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#FF4444' }}>
                    {formatNumber(monitorStatus.concurrentViewers)}
                  </div>
                </div>
                <div style={{ background: 'rgba(59,130,246,0.08)', borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#3B82F6', marginBottom: 2 }}>채팅 참여자</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3B82F6' }}>
                    {formatNumber(monitorStatus.chatParticipantCount)}
                  </div>
                </div>
                <div style={{ background: 'rgba(245,158,11,0.08)', borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#F59E0B', marginBottom: 2 }}>큰손 감지</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#F59E0B' }}>
                    {formatNumber(monitorStatus.activeBigSpenderCount)}
                  </div>
                </div>
              </div>

              {/* 채팅 참여자 목록 토글 */}
              <button
                className="btn btn-outline"
                style={{ width: '100%', padding: '6px', fontSize: '0.8rem' }}
                onClick={() => {
                  setShowParticipants(!showParticipants);
                  if (!showParticipants) fetchParticipants();
                }}
              >
                {showParticipants ? '채팅 참여자 접기' : `채팅 참여자 목록 보기 (${monitorStatus.chatParticipantCount}명)`}
              </button>

              {/* 채팅 참여자 목록 */}
              {showParticipants && (
                <div style={{ marginTop: 8, maxHeight: 250, overflowY: 'auto' }}>
                  {participants.length === 0 ? (
                    <div style={{ color: '#8B8B9E', textAlign: 'center', padding: 16, fontSize: '0.85rem' }}>
                      아직 채팅 참여자가 없습니다.
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: '0.75rem', color: '#8B8B9E' }}>닉네임</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: '0.75rem', color: '#8B8B9E' }}>메시지</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: '0.75rem', color: '#8B8B9E' }}>상태</th>
                        </tr>
                      </thead>
                      <tbody>
                        {participants.map((p) => (
                          <tr key={p.channelId} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '5px 6px', fontSize: '0.83rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {p.profileImage && (
                                  <img
                                    src={p.profileImage}
                                    alt=""
                                    style={{ width: 20, height: 20, borderRadius: '50%' }}
                                  />
                                )}
                                <span style={{ fontWeight: 500 }}>{p.displayName}</span>
                              </div>
                            </td>
                            <td style={{ textAlign: 'right', padding: '5px 6px', fontSize: '0.83rem', color: '#8B8B9E' }}>
                              {p.messageCount}회
                            </td>
                            <td style={{ textAlign: 'right', padding: '5px 6px', fontSize: '0.75rem' }}>
                              {p.isModerator && <span style={{ color: '#5B7FFF', marginRight: 4 }}>관리자</span>}
                              {p.isMember && <span style={{ color: '#2BA640' }}>멤버</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default YouTubeConnect;
